import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostBinding,
  OnChanges
} from '@angular/core';
import { ADS_ENABLED, AD_DEBUG_OUTLINE } from 'src/app/constants/ads.config';

@Component({
  selector: 'app-ad-banner',
  template: `
    <iframe
      #adFrame
      [style.width.px]="width"
      [style.height.px]="height"
      frameborder="0"
      scrolling="no"
      style="border:0; display:block; margin:1rem auto;"
    ></iframe>
    <!-- Off in every deployed build; see AD_DEBUG_OUTLINE in ads.config -->
    <small *ngIf="debugOutline" class="ad-debug-tag">banner {{ width }}×{{ height }}</small>
  `,
  styles: [`
    .ad-debug-tag {
      display: block; text-align: center; font: 11px/1.4 monospace;
      color: #d32f2f; letter-spacing: .04em;
    }
  `]
})
export class AdBannerComponent implements AfterViewInit, OnChanges {

  @Input() width!: number;
  @Input() height!: number;
  @Input() adKey!: string;

  /** Debug affordance, off in every deployed build. See AD_DEBUG_OUTLINE. */
  readonly debugOutline = AD_DEBUG_OUTLINE;
  @HostBinding('style.outline') get debugBorder(): string {
    return AD_DEBUG_OUTLINE ? '2px dashed #d32f2f' : '';
  }

  // Collapse the host element when ads are globally disabled (see ads.config).
  // While debugging, force a block box so the outline wraps the slot, not the line box.
  @HostBinding('style.display') get hostDisplay(): string {
    if (!ADS_ENABLED) { return 'none'; }
    return AD_DEBUG_OUTLINE ? 'block' : '';
  }

  @ViewChild('adFrame', { static: true }) adFrame!: ElementRef<HTMLIFrameElement>;

  /** ngOnChanges fires before the first ngAfterViewInit; writing to the iframe
      before then would be thrown away, so the first render is left to the latter. */
  private rendered = false;

  ngAfterViewInit(): void {
    this.rendered = true;
    this.render();
  }

  /** The parent swaps in a different creative when the layout crosses the mobile
      breakpoint. Without this, the iframe keeps the size and ad key it was first
      built with, and the box silently resizes around the wrong ad. */
  ngOnChanges(): void {
    if (this.rendered) { this.render(); }
  }

  /**
   * Hand the ad snippet to the iframe via `srcdoc` rather than doc.write().
   *
   * invoke.js paints its creative by calling `document.write()`. That only works
   * while the HTML parser is still open. The previous approach — doc.open(),
   * doc.write(html), doc.close() — closed the document before the *external*
   * invoke.js had loaded, so by the time it ran, its document.write() hit a
   * closed document: the browser then either ignores the write or implicitly
   * reopens and blanks the page. Net effect was a 200 on invoke.js and an empty
   * box, on every host. srcdoc parses the markup normally, so the script runs
   * mid-parse and its document.write() lands where it should.
   */
  private render(): void {
    if (!ADS_ENABLED) { return; }
    this.adFrame.nativeElement.srcdoc = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            html, body { margin:0; padding:0; overflow:hidden; }
          </style>
        </head>
        <body>
          <script type="text/javascript">
            atOptions = {
              'key': '${this.adKey}',
              'format': 'iframe',
              'width': ${this.width},
              'height': ${this.height},
              'params': {}
            };
          </script>
          <script type="text/javascript" src="https://www.highperformanceformat.com/${this.adKey}/invoke.js"></script>
        </body>
      </html>`;
  }
}

